import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import VoiceDictationInput from '../../components/VoiceDictationInput';
import SignatureInput from '../../components/SignatureInput';
import { FileText, Save, ArrowLeft, Printer, Plus, Trash2 } from 'lucide-react';
import { exportFillableFromDom } from '../../utils/fillable';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PCADocumentation = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const formId = searchParams.get('id');
  
  const [saving, setSaving] = useState(false);
  const [patients, setPatients] = useState([]);
  
  const [formData, setFormData] = useState({
    // Patient Information
    patientId: '',
    patientsName: '',
    medicalRecordNumber: '',
    dateOfBirth: '',
    diagnosis: '',
    allergies: '',
    physicianName: '',
    physicianPhone: '',
    
    // Visit Information
    visitDate: '',
    timeIn: '',
    timeOut: '',
    
    // PCA Services Provided
    personalCareAssistance: false,
    bathingAssistance: false,
    bathingAssistanceType: '',
    groomingAssistance: false,
    groomingAssistanceDetails: '',
    dressingAssistance: false,
    toiletingAssistance: false,
    transferAssistance: false,
    ambulationAssistance: false,
    feedingAssistance: false,
    medicationReminders: false,
    medicationRemindersDetails: '',
    
    // Vital Signs (if applicable)
    temperature: '',
    pulse: '',
    respiration: '',
    bloodPressure: '',
    weight: '',
    bloodSugar: '',
    oxygenSaturation: '',
    
    // Patient Condition
    skinConditionNormal: false,
    skinConditionAbnormal: false,
    skinConditionDetails: '',
    appetiteGood: false,
    appetiteFair: false,
    appetitePoor: false,
    mealIntake: '',
    fluidIntake: '',
    bowelMovement: false,
    bowelMovementDetails: '',
    urinaryOutput: '',
    
    // Mental Status
    alertOriented: false,
    confused: false,
    agitated: false,
    lethargic: false,
    mentalStatusOther: '',
    
    // Mobility Status
    ambulatoryIndependent: false,
    ambulatoryWithAssistance: false,
    wheelchairBound: false,
    bedbound: false,
    mobilityDeviceUsed: '',
    
    // Safety Observations
    homeEnvironmentSafe: false,
    fallRiskIdentified: false,
    fallPrecautionsInPlace: false,
    safetyHazardsNoted: '',
    
    // Communication
    communicationWithFamily: false,
    communicationWithNurse: false,
    communicationDetails: '',
    changesReportedToSupervisor: false,
    changesReportedDetails: '',
    
    // Activities Completed
    lightHousekeeping: false,
    laundry: false,
    mealPreparation: false,
    groceryShopping: false,
    errands: false,
    companionship: false,
    activitiesOther: '',
    
    // Patient/Caregiver Education
    educationProvided: false,
    educationTopics: '',
    patientCaregiverResponse: '',
    
    // Care Plan Compliance
    followingCarePlan: false,
    deviationsFromCarePlan: '',
    
    // Comments/Observations
    generalComments: '',
    concernsOrIssues: '',
    recommendationsForNextVisit: '',
    
    // Signatures (comprehensive - typed, voice recording, handwritten)
    pcaSignature: { typed: '', audioURL: null, canvasDataURL: null },
    pcaSignatureDate: '',
    patientSignature: { typed: '', audioURL: null, canvasDataURL: null },
    patientSignatureDate: '',
    supervisorSignature: { typed: '', audioURL: null, canvasDataURL: null },
    supervisorSignatureDate: '',
  });

  useEffect(() => {
    fetchPatients();
    if (formId) fetchForm();
  }, [formId]);

  const getToken = () => {
    const user = localStorage.getItem('ivory_user');
    if (user) {
      const userData = JSON.parse(user);
      return userData.token;
    }
    return localStorage.getItem('token');
  };

  const fetchPatients = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/patients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) setPatients(data.patients);
    } catch (error) {
      console.error('Error fetching patients:', error);
    }
  };

  const fetchForm = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/skilled-nursing/pca/${formId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success && data.form) {
        setFormData(prev => ({ ...prev, ...data.form.formData }));
      }
    } catch (error) {
      console.error('Error fetching form:', error);
      toast.error('Failed to load form');
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!formData.patientId) {
      toast.error('Please select a patient');
      return;
    }
    
    setSaving(true);
    try {
      const token = getToken();
      const url = formId 
        ? `${API_URL}/api/skilled-nursing/pca/${formId}`
        : `${API_URL}/api/skilled-nursing/pca`;
      
      const selectedPatient = patients.find(p => p.id === formData.patientId);
      
      const payload = {
        patientId: formData.patientId,
        patientName: selectedPatient ? `${selectedPatient.firstName} ${selectedPatient.lastName}` : formData.patientsName,
        formType: 'pca-documentation',
        formData: formData
      };
      
      const response = await fetch(url, {
        method: formId ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (data.success) {
        toast.success(formId ? 'Form updated' : 'Form created');
        if (!formId && data.form?.id) {
          navigate(`/dashboard/skilled-nursing/pca?id=${data.form.id}`);
        }
      } else {
        toast.error('Failed to save form');
      }
    } catch (error) {
      console.error('Error saving form:', error);
      toast.error('Failed to save form');
    } finally {
      setSaving(false);
    }
  };

  const CheckboxField = ({ label, checked, onChange, className = '' }) => (
    <label className={`flex items-center gap-2 cursor-pointer ${className}`}>
      <input 
        type="checkbox" 
        checked={checked || false} 
        onChange={(e) => onChange(e.target.checked)} 
        className="w-4 h-4 rounded border-gray-300"
      />
      <span className="text-sm">{label}</span>
    </label>
  );

  return (
    <DashboardLayout>
      <div className="p-6 max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => navigate(-1)} className="p-2">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <FileText className="w-8 h-8 text-[#f39c12]" />
              <div>
                <h1 className="text-2xl font-bold text-gray-800">PCA Documentation</h1>
                <p className="text-sm text-gray-500">Personal Care Assistant Visit Record</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => exportFillableFromDom(document.getElementById('main-content'), 'PCA_Documentation.pdf')}>
                <Printer className="w-4 h-4 mr-2" /> Print
              </Button>
              <Button onClick={handleSave} disabled={saving} className="bg-[#f39c12] hover:bg-[#e67e22]">
                <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </div>

        {/* Form Content */}
        <div className="bg-white rounded-lg shadow-sm border p-6 space-y-6">
          {/* Company Header */}
          <div className="text-center border-b pb-4">
            <h2 className="text-xl font-bold text-[#f39c12]">Ivory Home Health Care. Inc.</h2>
            <p className="text-xs text-gray-600">Ph: (562) 349 - 0860; fax: (562) 285 – 7216</p>
            <p className="text-xs text-gray-600">Email: office@ivory_hhc.com; ivory_hhc@qbhm.net</p>
            <h3 className="text-lg font-bold mt-2">Personal Care Assistant (PCA) Documentation</h3>
          </div>

          {/* Patient Information */}
          <div className="border rounded-lg p-4 bg-blue-50">
            <h4 className="font-bold text-lg mb-3 text-blue-800">Patient Information</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <Label className="font-bold">Patient Name</Label>
                <select
                  value={formData.patientId}
                  onChange={(e) => {
                    const patient = patients.find(p => p.id === e.target.value);
                    handleInputChange('patientId', e.target.value);
                    if (patient) handleInputChange('patientsName', `${patient.firstName} ${patient.lastName}`);
                  }}
                  className="w-full mt-1 px-3 py-2 border rounded-md"
                >
                  <option value="">Select Patient...</option>
                  {patients.map(p => (
                    <option key={p.id} value={p.id}>{p.firstName} {p.lastName}</option>
                  ))}
                </select>
              </div>
              <VoiceDictationInput label="Medical Record #" value={formData.medicalRecordNumber} onChange={(e) => handleInputChange('medicalRecordNumber', e.target.value)} />
              <VoiceDictationInput label="Date of Birth" value={formData.dateOfBirth} onChange={(e) => handleInputChange('dateOfBirth', e.target.value)} placeholder="MM/DD/YYYY" />
              <VoiceDictationInput label="Diagnosis" value={formData.diagnosis} onChange={(e) => handleInputChange('diagnosis', e.target.value)} />
              <VoiceDictationInput label="Allergies" value={formData.allergies} onChange={(e) => handleInputChange('allergies', e.target.value)} />
              <VoiceDictationInput label="Physician Name" value={formData.physicianName} onChange={(e) => handleInputChange('physicianName', e.target.value)} />
            </div>
          </div>

          {/* Visit Information */}
          <div className="border rounded-lg p-4 bg-green-50">
            <h4 className="font-bold text-lg mb-3 text-green-800">Visit Information</h4>
            <div className="grid grid-cols-3 gap-4">
              <VoiceDictationInput label="Visit Date" value={formData.visitDate} onChange={(e) => handleInputChange('visitDate', e.target.value)} placeholder="MM/DD/YYYY" />
              <VoiceDictationInput label="Time In" value={formData.timeIn} onChange={(e) => handleInputChange('timeIn', e.target.value)} placeholder="HH:MM" />
              <VoiceDictationInput label="Time Out" value={formData.timeOut} onChange={(e) => handleInputChange('timeOut', e.target.value)} placeholder="HH:MM" />
            </div>
          </div>

          {/* PCA Services Provided */}
          <div className="border rounded-lg p-4 bg-purple-50">
            <h4 className="font-bold text-lg mb-3 text-purple-800">PCA Services Provided</h4>
            <div className="space-y-4">
              <div className="flex flex-wrap gap-4">
                <CheckboxField label="Personal Care Assistance" checked={formData.personalCareAssistance} onChange={(v) => handleInputChange('personalCareAssistance', v)} />
                <CheckboxField label="Transfer Assistance" checked={formData.transferAssistance} onChange={(v) => handleInputChange('transferAssistance', v)} />
                <CheckboxField label="Ambulation Assistance" checked={formData.ambulationAssistance} onChange={(v) => handleInputChange('ambulationAssistance', v)} />
                <CheckboxField label="Feeding Assistance" checked={formData.feedingAssistance} onChange={(v) => handleInputChange('feedingAssistance', v)} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <CheckboxField label="Bathing Assistance" checked={formData.bathingAssistance} onChange={(v) => handleInputChange('bathingAssistance', v)} />
                  {formData.bathingAssistance && (
                    <VoiceDictationInput 
                      value={formData.bathingAssistanceType} 
                      onChange={(e) => handleInputChange('bathingAssistanceType', e.target.value)}
                      placeholder="Type: Bed bath, shower, tub..."
                      className="mt-2"
                    />
                  )}
                </div>
                <div>
                  <CheckboxField label="Grooming Assistance" checked={formData.groomingAssistance} onChange={(v) => handleInputChange('groomingAssistance', v)} />
                  {formData.groomingAssistance && (
                    <VoiceDictationInput 
                      value={formData.groomingAssistanceDetails} 
                      onChange={(e) => handleInputChange('groomingAssistanceDetails', e.target.value)}
                      placeholder="Details..."
                      className="mt-2"
                    />
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-4">
                <CheckboxField label="Dressing Assistance" checked={formData.dressingAssistance} onChange={(v) => handleInputChange('dressingAssistance', v)} />
                <CheckboxField label="Toileting Assistance" checked={formData.toiletingAssistance} onChange={(v) => handleInputChange('toiletingAssistance', v)} />
              </div>
              <div>
                <CheckboxField label="Medication Reminders" checked={formData.medicationReminders} onChange={(v) => handleInputChange('medicationReminders', v)} />
                {formData.medicationReminders && (
                  <VoiceDictationInput 
                    value={formData.medicationRemindersDetails} 
                    onChange={(e) => handleInputChange('medicationRemindersDetails', e.target.value)}
                    placeholder="Medication details..."
                    className="mt-2"
                  />
                )}
              </div>
            </div>
          </div>

          {/* Vital Signs */}
          <div className="border rounded-lg p-4 bg-yellow-50">
            <h4 className="font-bold text-lg mb-3 text-yellow-800">Vital Signs (if applicable)</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <VoiceDictationInput label="Temperature" value={formData.temperature} onChange={(e) => handleInputChange('temperature', e.target.value)} placeholder="°F" />
              <VoiceDictationInput label="Pulse" value={formData.pulse} onChange={(e) => handleInputChange('pulse', e.target.value)} placeholder="bpm" />
              <VoiceDictationInput label="Respiration" value={formData.respiration} onChange={(e) => handleInputChange('respiration', e.target.value)} placeholder="/min" />
              <VoiceDictationInput label="Blood Pressure" value={formData.bloodPressure} onChange={(e) => handleInputChange('bloodPressure', e.target.value)} placeholder="mmHg" />
              <VoiceDictationInput label="Weight" value={formData.weight} onChange={(e) => handleInputChange('weight', e.target.value)} placeholder="lbs" />
              <VoiceDictationInput label="Blood Sugar" value={formData.bloodSugar} onChange={(e) => handleInputChange('bloodSugar', e.target.value)} placeholder="mg/dL" />
              <VoiceDictationInput label="O2 Saturation" value={formData.oxygenSaturation} onChange={(e) => handleInputChange('oxygenSaturation', e.target.value)} placeholder="%" />
            </div>
          </div>

          {/* Patient Condition */}
          <div className="border rounded-lg p-4 bg-orange-50">
            <h4 className="font-bold text-lg mb-3 text-orange-800">Patient Condition</h4>
            <div className="space-y-4">
              <div>
                <Label className="font-semibold">Skin Condition:</Label>
                <div className="flex gap-4 mt-2">
                  <CheckboxField label="Normal" checked={formData.skinConditionNormal} onChange={(v) => handleInputChange('skinConditionNormal', v)} />
                  <CheckboxField label="Abnormal" checked={formData.skinConditionAbnormal} onChange={(v) => handleInputChange('skinConditionAbnormal', v)} />
                </div>
                {formData.skinConditionAbnormal && (
                  <VoiceDictationInput 
                    value={formData.skinConditionDetails} 
                    onChange={(e) => handleInputChange('skinConditionDetails', e.target.value)}
                    placeholder="Describe abnormalities..."
                    className="mt-2"
                  />
                )}
              </div>
              <div>
                <Label className="font-semibold">Appetite:</Label>
                <div className="flex gap-4 mt-2">
                  <CheckboxField label="Good" checked={formData.appetiteGood} onChange={(v) => handleInputChange('appetiteGood', v)} />
                  <CheckboxField label="Fair" checked={formData.appetiteFair} onChange={(v) => handleInputChange('appetiteFair', v)} />
                  <CheckboxField label="Poor" checked={formData.appetitePoor} onChange={(v) => handleInputChange('appetitePoor', v)} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <VoiceDictationInput label="Meal Intake" value={formData.mealIntake} onChange={(e) => handleInputChange('mealIntake', e.target.value)} placeholder="Describe intake..." />
                <VoiceDictationInput label="Fluid Intake" value={formData.fluidIntake} onChange={(e) => handleInputChange('fluidIntake', e.target.value)} placeholder="Amount..." />
              </div>
              <div>
                <CheckboxField label="Bowel Movement" checked={formData.bowelMovement} onChange={(v) => handleInputChange('bowelMovement', v)} />
                {formData.bowelMovement && (
                  <VoiceDictationInput 
                    value={formData.bowelMovementDetails} 
                    onChange={(e) => handleInputChange('bowelMovementDetails', e.target.value)}
                    placeholder="Details..."
                    className="mt-2"
                  />
                )}
              </div>
              <VoiceDictationInput label="Urinary Output" value={formData.urinaryOutput} onChange={(e) => handleInputChange('urinaryOutput', e.target.value)} />
            </div>
          </div>

          {/* Mental Status */}
          <div className="border rounded-lg p-4 bg-teal-50">
            <h4 className="font-bold text-lg mb-3 text-teal-800">Mental Status</h4>
            <div className="flex flex-wrap gap-4">
              <CheckboxField label="Alert & Oriented" checked={formData.alertOriented} onChange={(v) => handleInputChange('alertOriented', v)} />
              <CheckboxField label="Confused" checked={formData.confused} onChange={(v) => handleInputChange('confused', v)} />
              <CheckboxField label="Agitated" checked={formData.agitated} onChange={(v) => handleInputChange('agitated', v)} />
              <CheckboxField label="Lethargic" checked={formData.lethargic} onChange={(v) => handleInputChange('lethargic', v)} />
            </div>
            <VoiceDictationInput label="Other observations:" value={formData.mentalStatusOther} onChange={(e) => handleInputChange('mentalStatusOther', e.target.value)} className="mt-2" />
          </div>

          {/* Mobility Status */}
          <div className="border rounded-lg p-4 bg-indigo-50">
            <h4 className="font-bold text-lg mb-3 text-indigo-800">Mobility Status</h4>
            <div className="flex flex-wrap gap-4 mb-3">
              <CheckboxField label="Ambulatory Independent" checked={formData.ambulatoryIndependent} onChange={(v) => handleInputChange('ambulatoryIndependent', v)} />
              <CheckboxField label="Ambulatory With Assistance" checked={formData.ambulatoryWithAssistance} onChange={(v) => handleInputChange('ambulatoryWithAssistance', v)} />
              <CheckboxField label="Wheelchair Bound" checked={formData.wheelchairBound} onChange={(v) => handleInputChange('wheelchairBound', v)} />
              <CheckboxField label="Bedbound" checked={formData.bedbound} onChange={(v) => handleInputChange('bedbound', v)} />
            </div>
            <VoiceDictationInput label="Mobility Device Used:" value={formData.mobilityDeviceUsed} onChange={(e) => handleInputChange('mobilityDeviceUsed', e.target.value)} />
          </div>

          {/* Safety Observations */}
          <div className="border rounded-lg p-4 bg-red-50">
            <h4 className="font-bold text-lg mb-3 text-red-800">Safety Observations</h4>
            <div className="flex flex-wrap gap-4 mb-3">
              <CheckboxField label="Home Environment Safe" checked={formData.homeEnvironmentSafe} onChange={(v) => handleInputChange('homeEnvironmentSafe', v)} />
              <CheckboxField label="Fall Risk Identified" checked={formData.fallRiskIdentified} onChange={(v) => handleInputChange('fallRiskIdentified', v)} />
              <CheckboxField label="Fall Precautions In Place" checked={formData.fallPrecautionsInPlace} onChange={(v) => handleInputChange('fallPrecautionsInPlace', v)} />
            </div>
            <VoiceDictationInput label="Safety Hazards Noted:" value={formData.safetyHazardsNoted} onChange={(e) => handleInputChange('safetyHazardsNoted', e.target.value)} rows={2} />
          </div>

          {/* Activities Completed */}
          <div className="border rounded-lg p-4 bg-lime-50">
            <h4 className="font-bold text-lg mb-3 text-lime-800">Activities Completed</h4>
            <div className="flex flex-wrap gap-4 mb-3">
              <CheckboxField label="Light Housekeeping" checked={formData.lightHousekeeping} onChange={(v) => handleInputChange('lightHousekeeping', v)} />
              <CheckboxField label="Laundry" checked={formData.laundry} onChange={(v) => handleInputChange('laundry', v)} />
              <CheckboxField label="Meal Preparation" checked={formData.mealPreparation} onChange={(v) => handleInputChange('mealPreparation', v)} />
              <CheckboxField label="Grocery Shopping" checked={formData.groceryShopping} onChange={(v) => handleInputChange('groceryShopping', v)} />
              <CheckboxField label="Errands" checked={formData.errands} onChange={(v) => handleInputChange('errands', v)} />
              <CheckboxField label="Companionship" checked={formData.companionship} onChange={(v) => handleInputChange('companionship', v)} />
            </div>
            <VoiceDictationInput label="Other Activities:" value={formData.activitiesOther} onChange={(e) => handleInputChange('activitiesOther', e.target.value)} />
          </div>

          {/* Communication */}
          <div className="border rounded-lg p-4 bg-cyan-50">
            <h4 className="font-bold text-lg mb-3 text-cyan-800">Communication</h4>
            <div className="flex flex-wrap gap-4 mb-3">
              <CheckboxField label="Communication with Family" checked={formData.communicationWithFamily} onChange={(v) => handleInputChange('communicationWithFamily', v)} />
              <CheckboxField label="Communication with Nurse" checked={formData.communicationWithNurse} onChange={(v) => handleInputChange('communicationWithNurse', v)} />
            </div>
            <VoiceDictationInput label="Communication Details:" value={formData.communicationDetails} onChange={(e) => handleInputChange('communicationDetails', e.target.value)} rows={2} />
            <div className="mt-3">
              <CheckboxField label="Changes Reported to Supervisor" checked={formData.changesReportedToSupervisor} onChange={(v) => handleInputChange('changesReportedToSupervisor', v)} />
              {formData.changesReportedToSupervisor && (
                <VoiceDictationInput 
                  value={formData.changesReportedDetails} 
                  onChange={(e) => handleInputChange('changesReportedDetails', e.target.value)}
                  placeholder="Details of changes reported..."
                  className="mt-2"
                />
              )}
            </div>
          </div>

          {/* Care Plan Compliance */}
          <div className="border rounded-lg p-4 bg-pink-50">
            <h4 className="font-bold text-lg mb-3 text-pink-800">Care Plan Compliance</h4>
            <CheckboxField label="Following Care Plan" checked={formData.followingCarePlan} onChange={(v) => handleInputChange('followingCarePlan', v)} />
            <VoiceDictationInput label="Deviations from Care Plan:" value={formData.deviationsFromCarePlan} onChange={(e) => handleInputChange('deviationsFromCarePlan', e.target.value)} rows={2} className="mt-2" />
          </div>

          {/* Comments/Observations */}
          <div className="border rounded-lg p-4 bg-gray-50">
            <h4 className="font-bold text-lg mb-3">Comments/Observations</h4>
            <div className="space-y-4">
              <VoiceDictationInput label="General Comments:" value={formData.generalComments} onChange={(e) => handleInputChange('generalComments', e.target.value)} rows={3} />
              <VoiceDictationInput label="Concerns or Issues:" value={formData.concernsOrIssues} onChange={(e) => handleInputChange('concernsOrIssues', e.target.value)} rows={2} />
              <VoiceDictationInput label="Recommendations for Next Visit:" value={formData.recommendationsForNextVisit} onChange={(e) => handleInputChange('recommendationsForNextVisit', e.target.value)} rows={2} />
            </div>
          </div>

          {/* Signatures - Comprehensive with Type/Voice Recording/Handwritten */}
          <div className="border rounded-lg p-4 bg-gray-100">
            <h4 className="font-bold text-lg mb-3">SIGNATURES</h4>
            <p className="text-sm text-gray-600 mb-4">Choose your preferred signature method: Type, Voice Recording, or Handwritten</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="space-y-3">
                <SignatureInput 
                  label="PCA Signature" 
                  value={formData.pcaSignature} 
                  onChange={(value) => handleInputChange('pcaSignature', value)} 
                />
                <VoiceDictationInput label="Date" value={formData.pcaSignatureDate} onChange={(e) => handleInputChange('pcaSignatureDate', e.target.value)} placeholder="MM/DD/YYYY" />
              </div>
              <div className="space-y-3">
                <SignatureInput 
                  label="Patient Signature" 
                  value={formData.patientSignature} 
                  onChange={(value) => handleInputChange('patientSignature', value)} 
                />
                <VoiceDictationInput label="Date" value={formData.patientSignatureDate} onChange={(e) => handleInputChange('patientSignatureDate', e.target.value)} placeholder="MM/DD/YYYY" />
              </div>
              <div className="space-y-3">
                <SignatureInput 
                  label="Supervisor Signature" 
                  value={formData.supervisorSignature} 
                  onChange={(value) => handleInputChange('supervisorSignature', value)} 
                />
                <VoiceDictationInput label="Date" value={formData.supervisorSignatureDate} onChange={(e) => handleInputChange('supervisorSignatureDate', e.target.value)} placeholder="MM/DD/YYYY" />
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#f39c12] hover:bg-[#e67e22]">
              <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Form'}
            </Button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default PCADocumentation;
